import { QSetStatus } from '@report/graphql';
import { Button, Divider, Flex, Form, FormSearchSelection, Grid, Text } from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useQuestionUsageByQSetReportSearchParams } from '../../hooks';

import {
  type QuestionUsageByQSetReportFilterFormInput,
  questionUsageByQSetReportFilterFormSchema,
} from './schema';

const QSetStatusOptions = [
  { label: 'Published', value: QSetStatus.Published },
  { label: 'Disabled', value: QSetStatus.Disabled },
];

interface QuestionUsageByQSetReportFilterSectionProps {
  examCoursesOptions: { label: string; value: string }[];
  loading: boolean;
}

export default function QuestionUsageByQSetReportFilterSection(
  props: Readonly<QuestionUsageByQSetReportFilterSectionProps>,
) {
  const { examCoursesOptions, loading } = props;
  const [{ examCourse, qSetStatus }, { changeFilterParams }] =
    useQuestionUsageByQSetReportSearchParams();

  const formContext = useForm<QuestionUsageByQSetReportFilterFormInput>({
    resolver: zodResolver(questionUsageByQSetReportFilterFormSchema),
    defaultValues: {
      examCourse: undefined,
      qSetStatus: undefined,
    },
  });
  useEffect(() => {
    formContext.reset({
      examCourse: examCourse
        ? {
            label: examCoursesOptions.find((option) => option.value === examCourse)?.label ?? '',
            value: examCourse,
          }
        : undefined,
      qSetStatus: qSetStatus
        ? QSetStatusOptions.find((option) => option.value === qSetStatus)
        : undefined,
    });
  }, [examCourse, qSetStatus, formContext, examCoursesOptions]);

  const onSubmit = (data: QuestionUsageByQSetReportFilterFormInput) => {
    changeFilterParams({
      examCourse: data.examCourse?.value ?? null,
      qSetStatus: data.qSetStatus.value ?? null,
      page: 0,
      pageSize: 50,
    });
  };

  const handleReset = () => {
    formContext.reset({
      examCourse: undefined,
      qSetStatus: undefined,
    });
    changeFilterParams({
      examCourse: '',
      qSetStatus: null,
      page: 0,
      pageSize: 50,
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={formContext.handleSubmit(onSubmit)}
        sx={(theme) => ({
          p: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
      >
        <Grid
          sx={(theme) => ({
            columnGap: theme.spacing(6),
            rowGap: theme.spacing(6),
            gridTemplateColumns: '160px 1fr',
          })}
        >
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Filters:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              width: '100%',
              justifyContent: 'space-between',
              flex: 1,
              alignItems: 'flex-start',
            })}
          >
            <FormSearchSelection
              name='examCourse'
              label='Exam Course'
              placeholder='เลือก Exam Course'
              options={examCoursesOptions}
              loading={loading}
              sx={{ width: '100%' }}
            />
            <FormSearchSelection
              name='qSetStatus'
              label='สถานะชุดข้อสอบ'
              placeholder='เลือกสถานะชุดข้อสอบ'
              options={QSetStatusOptions}
              sx={{ width: '100%' }}
            />
          </Flex>
        </Grid>
        <Divider />
        <Flex sx={{ gap: (theme) => theme.spacing(2), justifyContent: 'end' }}>
          <Button color='secondary' variant='outlined' onClick={() => handleReset()}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
